import { Component, Input, OnChanges, SimpleChanges, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MaterialModule } from '@app/material.module';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User } from '@interfaces/user';
import { UserApiService} from '@services/api/user/user/user-api.service';
import { UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import { Subject, takeUntil} from 'rxjs';
import { ToastrServices} from '@services/toastr/toastr.service';
import {Prefix} from '@interfaces/prefix';
import {SettingsApiService} from '@services/api/settings/settings-api.service';
import {phoneValidator} from '@core/shared/form-validators';

@Component({
  selector: 'app-personal-details',
  imports: [MaterialModule, ReactiveFormsModule, CommonModule, TranslateModule, FormsModule],
  templateUrl: './personal-details.component.html',
  standalone: true,
  styleUrl: './personal-details.component.scss'
})
export class PersonalDetailsComponent implements OnChanges, OnInit {
  @Input() user: User | null = null;
  userDetailForm: FormGroup;
  private destroy$ = new Subject<void>();
  private user_api_service = inject(UserApiService);
  private user_storage_service = inject(UserStorageService);
  private prefix_api_service = inject(SettingsApiService);
  private toastr_service = inject(ToastrServices);
  private translate = inject(TranslateService);
  protected prefixes: Prefix[] = [];
  prefixFilter: string = '';
  @ViewChild('filterInput') filterInputRef!: ElementRef<HTMLInputElement>;



  constructor(private fb: FormBuilder) {
    this.userDetailForm = this.fb.group({
      name: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      username: ['', Validators.required],
      phone: ['', phoneValidator()],
      prefix: [''],
    });
  }

  ngOnInit(): void {
    this.loadPrefixes();
  }

  loadPrefixes(): void {
    this.prefix_api_service.get_prefixes().subscribe(
      (response) => {
        this.prefixes = response.data;
        if (!this.userDetailForm.get('prefix')?.value) {
          this.setDefaultPrefix();
        }
      },
      (error) => {
        console.error('Error loading prefixes:', error);
      }
    );
  }

  filteredPrefixes(): Prefix[] {
    if (!this.prefixFilter) return this.prefixes;
    const lower = this.prefixFilter.toLowerCase();
    return this.prefixes.filter(p => p.prefix.toLowerCase().includes(lower));
  }

  onPrefixDropdownOpened(opened: boolean): void {
    if (!opened) {
      this.prefixFilter = '';
    } else {
      setTimeout(() => {
        this.filterInputRef?.nativeElement?.focus();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && changes['user'].currentValue) {
      this.userDetailForm.patchValue({
        name: changes['user'].currentValue.name,
        lastname: changes['user'].currentValue.lastname,
        email: changes['user'].currentValue.email,
        username: changes['user'].currentValue.username,
        phone: changes['user'].currentValue.phone,
        prefix: changes['user'].currentValue.prefix?.id,
      });
      if (!this.userDetailForm.get('prefix')?.value) {
        this.setDefaultPrefix();
      }
    } else {
      this.userDetailForm.reset();
    }
  }

  setDefaultPrefix(): void {
    const defaultPrefix = this.prefixes.find(prefix => prefix.default === true);
    if (defaultPrefix) {
      this.userDetailForm.controls['prefix'].setValue(defaultPrefix.id);
    }
  }

  onSubmit(): void {

    if (this.userDetailForm.valid) {
      this.user_api_service.updateUser(
        this.userDetailForm.get('name')?.value,
        this.userDetailForm.get('lastname')?.value,
        this.userDetailForm.get('email')?.value,
        this.userDetailForm.get('username')?.value,
        this.userDetailForm.get('phone')?.value,
        this.userDetailForm.get('prefix')?.value
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.user_storage_service.setUserStorage(response.data);
            this.toastr_service.showSuccess(this.translate.instant('alert.success_user_update'), this.translate.instant('alert.success'));
          },
          error: (error) => {
            console.error('Error:', error);
            this.toastr_service.showError(this.translate.instant('alert.error_user_update'), this.translate.instant('alert.error'));
          }
        });
    } else {
      this.userDetailForm.markAllAsTouched();
    }
  }

  resetForm(): void {
    if (this.user) {
      this.userDetailForm.patchValue({
        name: this.user.name,
        lastname: this.user.lastname,
        email: this.user.email,
        username: this.user.username,
        phone: this.user.phone,
        prefix: this.user.prefix?.id
      });
      if (!this.userDetailForm.get('prefix')?.value) {
        this.setDefaultPrefix();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPhoneInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    const numericValue = value.replace(/[^0-9+\- ]/g, '');
    if (value !== numericValue) {
      inputElement.value = numericValue;
      this.userDetailForm.controls['phone'].setValue(numericValue);
    }
  }
}
